/**
 * Hook genérico para gestión de vistas (tabs) en listas.
 * Misma lógica que useUsersViews: vistas de sistema + personalizadas, crear/guardar/duplicar/renombrar/eliminar.
 * Usar en Categorías y en cualquier lista que no sea Usuarios.
 *
 * Opcional (estilo Odoo):
 * - `groupBy`/`setGroupBy`: si se pasan, cada vista guarda y restaura su agrupación (Group By).
 * - "Filtro por defecto": una vista personalizada marcada `isDefault` se carga automáticamente
 *   al entrar a la lista (ver `setViewAsDefault`). Solo una vista puede ser la predeterminada.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { TabProps } from '@/lib/polaris';
import { IndexFiltersMode } from '@/lib/polaris';
import { StarIcon, StarFilledIcon } from '@/lib/polaris-icons';
import {
  loadListCustomViews,
  saveListCustomViews,
  type ListViewGeneric,
} from '@/lib/listViewsStorage';

export interface UseListViewsOptions {
  listId: string;
  tenantId: string | undefined;
  systemViews: ListViewGeneric[];
  searchValue: string;
  filters: Record<string, string[]>;
  setSearchValue: (value: string) => void;
  setFilters: (f: Record<string, string[]>) => void;
  setMode: (mode: IndexFiltersMode) => void;
  mode: IndexFiltersMode;
  /** Opcional: niveles de agrupación (Group By). Si se pasan, las vistas guardan/restauran groupBy. */
  groupBy?: string[];
  setGroupBy?: (g: string[]) => void;
}

const cloneFilterValues = (fv: Record<string, string[]>): Record<string, string[]> =>
  Object.fromEntries(
    Object.entries(fv).map(([k, arr]) => [k, Array.isArray(arr) ? [...arr] : []])
  );

export function useListViews(options: UseListViewsOptions) {
  const {
    listId,
    tenantId,
    systemViews,
    searchValue,
    filters,
    setSearchValue,
    setFilters,
    setMode,
    mode,
    groupBy,
    setGroupBy,
  } = options;

  const [customViews, setCustomViews] = useState<ListViewGeneric[]>(() =>
    loadListCustomViews(listId, tenantId ?? undefined)
  );
  // Vista inicial: si hay una marcada como "filtro por defecto" se abre con ella; si no, la 1ª de sistema.
  const [activeViewId, setActiveViewId] = useState<string>(() => {
    const loaded = loadListCustomViews(listId, tenantId ?? undefined);
    const def = loaded.find((v) => v.isDefault);
    return def?.id ?? systemViews[0]?.id ?? 'todos';
  });

  const allViews = useMemo(
    () => [...systemViews, ...customViews],
    [systemViews, customViews]
  );
  const activeView =
    allViews.find((v) => v.id === activeViewId) ?? systemViews[0];

  const allViewsRef = useRef(allViews);
  allViewsRef.current = allViews;
  // Ref para no meter setGroupBy en deps de efectos/callbacks (evita rebotes; igual que allViewsRef).
  const setGroupByRef = useRef(setGroupBy);
  setGroupByRef.current = setGroupBy;

  useEffect(() => {
    setCustomViews(loadListCustomViews(listId, tenantId ?? undefined));
  }, [listId, tenantId]);

  // Aplicar estado de la vista solo al cambiar de tab (activeViewId), no en cada render.
  // Si dependiéramos de allViews, al tener SYSTEM_VIEWS inline se crearía nueva referencia cada render
  // y se resetearían búsqueda/filtros al hacer clic en los checkboxes del filtro (rebote).
  useEffect(() => {
    const v = allViewsRef.current.find((x) => x.id === activeViewId);
    if (!v) return;
    setSearchValue(v.query);
    setFilters(cloneFilterValues(v.filterValues));
    setGroupByRef.current?.(v.groupBy ? [...v.groupBy] : []);
  }, [activeViewId, setSearchValue, setFilters]);

  const currentGroupBy = useMemo(() => groupBy ?? [], [groupBy]);

  const currentViewState = useMemo(
    () => ({
      query: searchValue,
      filterValues: { ...filters },
      groupBy: [...currentGroupBy],
    }),
    [searchValue, filters, currentGroupBy]
  );

  const hasUnsavedFilterChanges =
    activeView.query !== currentViewState.query ||
    JSON.stringify(activeView.filterValues) !== JSON.stringify(currentViewState.filterValues) ||
    JSON.stringify(activeView.groupBy ?? []) !== JSON.stringify(currentViewState.groupBy);

  const applyViewState = useCallback(
    (v: ListViewGeneric) => {
      setSearchValue(v.query);
      setFilters(cloneFilterValues(v.filterValues));
      setGroupByRef.current?.(v.groupBy ? [...v.groupBy] : []);
    },
    [setSearchValue, setFilters]
  );

  const handleSwitchView = useCallback(
    (index: number) => {
      const v = allViews[index];
      if (!v) return;
      setActiveViewId(v.id);
      applyViewState(v);
      setMode(IndexFiltersMode.Default);
    },
    [allViews, applyViewState, setMode]
  );

  const handleCancelFilter = useCallback(() => {
    if (activeView) applyViewState(activeView);
    setMode(IndexFiltersMode.Default);
  }, [activeView, applyViewState, setMode]);

  const handleSaveView = useCallback(async () => {
    if (!activeView || activeView.isSystem) return true;
    const updated: ListViewGeneric = {
      ...activeView,
      query: searchValue,
      filterValues: cloneFilterValues(filters),
      groupBy: [...currentGroupBy],
    };
    setCustomViews((prev) => {
      const next = prev.map((v) => (v.id === activeViewId ? updated : v));
      saveListCustomViews(listId, tenantId ?? undefined, next);
      return next;
    });
    setMode(IndexFiltersMode.Default);
    return true;
  }, [activeView, activeViewId, searchValue, filters, currentGroupBy, listId, tenantId, setMode]);

  const createViewWithName = useCallback(
    (name: string) => {
      const label = (name || 'Vista nueva').trim();
      const newView: ListViewGeneric = {
        id: `custom-${Date.now()}`,
        label,
        isSystem: false,
        query: searchValue,
        filterValues: cloneFilterValues(filters),
        groupBy: [...currentGroupBy],
      };
      setCustomViews((prev) => {
        const next = [...prev, newView];
        saveListCustomViews(listId, tenantId ?? undefined, next);
        return next;
      });
      setActiveViewId(newView.id);
      applyViewState(newView);
      setMode(IndexFiltersMode.Default);
    },
    [listId, tenantId, searchValue, filters, currentGroupBy, setMode, applyViewState]
  );

  const doRenameView = useCallback(
    (viewId: string, newName: string) => {
      const name = (newName || 'Vista').trim();
      setCustomViews((prev) => {
        const next = prev.map((v) => (v.id === viewId ? { ...v, label: name } : v));
        saveListCustomViews(listId, tenantId ?? undefined, next);
        return next;
      });
    },
    [listId, tenantId]
  );

  const doDuplicateView = useCallback(
    (viewId: string, newName: string) => {
      const v = allViews.find((x) => x.id === viewId);
      if (!v) return;
      const label = (newName || `${v.label} (copia)`).trim();
      const newView: ListViewGeneric = {
        id: `custom-${Date.now()}`,
        label,
        isSystem: false,
        query: v.query,
        filterValues: cloneFilterValues(v.filterValues),
        groupBy: v.groupBy ? [...v.groupBy] : [],
        // La copia NO hereda "filtro por defecto" (solo una vista puede serlo).
      };
      setCustomViews((prev) => {
        const next = [...prev, newView];
        saveListCustomViews(listId, tenantId ?? undefined, next);
        return next;
      });
      setActiveViewId(newView.id);
      applyViewState(newView);
    },
    [allViews, listId, tenantId, applyViewState]
  );

  const doDeleteView = useCallback(
    (viewId: string) => {
      setCustomViews((prev) => {
        const next = prev.filter((v) => v.id !== viewId);
        saveListCustomViews(listId, tenantId ?? undefined, next);
        return next;
      });
      if (activeViewId === viewId && systemViews[0]) {
        setActiveViewId(systemViews[0].id);
        applyViewState(systemViews[0]);
      }
    },
    [activeViewId, listId, tenantId, systemViews, applyViewState]
  );

  // Marca/desmarca una vista personalizada como "filtro por defecto" (solo una a la vez).
  // La predeterminada se abre automáticamente al entrar a la lista.
  const setViewAsDefault = useCallback(
    (viewId: string, makeDefault: boolean) => {
      setCustomViews((prev) => {
        const next = prev.map((v) => ({
          ...v,
          isDefault: v.id === viewId ? makeDefault : false,
        }));
        saveListCustomViews(listId, tenantId ?? undefined, next);
        return next;
      });
    },
    [listId, tenantId]
  );

  const selectedTabIndex = allViews.findIndex((v) => v.id === activeViewId);

  const indexFiltersTabs: TabProps[] = useMemo(
    () =>
      allViews.map((v) => ({
        id: v.id,
        content: v.label,
        isLocked: false,
        panelID: `panel-${listId}-${v.id}`,
        // ★ indica la vista marcada como filtro por defecto (se abre al entrar).
        badge: v.isDefault ? '★' : undefined,
        actions: v.isSystem
          ? [
              {
                type: 'duplicate' as const,
                onPrimaryAction: async (value: string) => {
                  doDuplicateView(v.id, value);
                  return true;
                },
              },
            ]
          : [
              {
                // 'edit' NO abre modal en Polaris (isModalActivator = !type.includes('edit')):
                // lo usamos como vehículo para un toggle custom "filtro por defecto".
                type: 'edit' as const,
                content: v.isDefault ? 'Quitar de filtro por defecto' : 'Marcar como filtro por defecto',
                icon: v.isDefault ? StarFilledIcon : StarIcon,
                onAction: () => setViewAsDefault(v.id, !v.isDefault),
              },
              {
                type: 'rename' as const,
                onPrimaryAction: async (value: string) => {
                  doRenameView(v.id, value);
                  return true;
                },
              },
              {
                type: 'duplicate' as const,
                onPrimaryAction: async (value: string) => {
                  doDuplicateView(v.id, value);
                  return true;
                },
              },
              {
                type: 'delete' as const,
                onPrimaryAction: async () => {
                  doDeleteView(v.id);
                  return true;
                },
              },
            ],
      })),
    [allViews, listId, doRenameView, doDuplicateView, doDeleteView, setViewAsDefault]
  );

  return {
    allViews,
    activeView,
    activeViewId,
    selectedTabIndex,
    handleSwitchView,
    handleCancelFilter,
    handleSaveView,
    createViewWithName,
    doRenameView,
    doDuplicateView,
    doDeleteView,
    setViewAsDefault,
    hasUnsavedFilterChanges,
    indexFiltersTabs,
  };
}
